/**
 * GA4 (gtag) for sushiking.com.pl — consent-aware, SPA page views, ecommerce helpers.
 * Measurement ID can be overridden via VITE_GA_MEASUREMENT_ID.
 */

import type { CartItem } from '@/types'

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

// Align with backend SUSHIKING_GA_MEASUREMENT_ID / Google stream "sushiking"
const GA_MEASUREMENT_ID = import.meta.env?.VITE_GA_MEASUREMENT_ID?.trim() || 'G-K6VK311R3H'

const CONSENT_COOKIE = 'sushiking_cookie_consent'

function isGaDebugEnabled(): boolean {
  return import.meta.env?.VITE_GA_DEBUG === 'true'
}

function shouldRunGa(): boolean {
  if (typeof window === 'undefined') return false
  if (import.meta.env?.DEV && !isGaDebugEnabled()) return false
  return true
}

function getStoredConsent(): { preferences?: Record<string, boolean> } | null {
  try {
    const raw = `; ${document.cookie}`
    const parts = raw.split(`; ${CONSENT_COOKIE}=`)
    if (parts.length !== 2) return null
    const value = decodeURIComponent(parts.pop()?.split(';').shift() || '')
    return JSON.parse(value) as { preferences?: Record<string, boolean> }
  } catch {
    return null
  }
}

/** Functional cookies toggle covers "analytics" in the banner copy; accept-all also sets analytics. */
export function hasAnalyticsConsent(): boolean {
  const c = getStoredConsent()
  const p = c?.preferences
  if (!p) return false
  return !!(p.functional || p.analytics)
}

export function hasAdsConsent(): boolean {
  const p = getStoredConsent()?.preferences
  return !!p?.marketing
}

export function installConsentDefaults(): void {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  const gtag: (...a: unknown[]) => void = function gtag(...args: unknown[]) {
    window.dataLayer.push(args)
  }
  window.gtag = gtag

  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    wait_for_update: 500,
  })
}

function applyConsentFromCookie(): void {
  if (!window.gtag) return
  const analytics = hasAnalyticsConsent()
  const ads = hasAdsConsent()
  window.gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: ads ? 'granted' : 'denied',
    ad_user_data: ads ? 'granted' : 'denied',
    ad_personalization: ads ? 'granted' : 'denied',
  })
}

function injectGaScript(): void {
  if (!shouldRunGa()) return
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`)) {
    return
  }
  const s = document.createElement('script')
  s.async = true
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  document.head.appendChild(s)
}

/**
 * Call once at app startup (after installConsentDefaults). Loads gtag.js only when measurement is allowed.
 */
export function initGoogleAnalytics(): void {
  if (typeof window === 'undefined') return
  installConsentDefaults()

  if (!hasAnalyticsConsent() && !hasAdsConsent()) {
    return
  }

  applyConsentFromCookie()
  injectGaScript()

  window.gtag!('js', new Date())
  window.gtag!('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
  })
}

export function trackPageView(pathname: string): void {
  if (!shouldRunGa() || !window.gtag) return
  if (!hasAnalyticsConsent()) return

  const page_path = pathname || window.location.pathname
  window.gtag('event', 'page_view', {
    page_path,
    page_location: window.location.href.split(/[?#]/)[0],
    page_title: typeof document !== 'undefined' ? document.title : '',
  })
}

export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  if (!shouldRunGa() || !window.gtag) return
  if (!hasAnalyticsConsent()) return
  window.gtag('event', name, params)
}

export function setGaUserId(userId: string | number | null): void {
  if (!shouldRunGa() || !window.gtag) return
  if (!hasAnalyticsConsent()) return
  const id = userId != null ? String(userId) : undefined
  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: id,
  })
}

function normalizeCartItemsForGa(items: CartItem[]) {
  return items.map((item) => ({
    item_id: item.isCustomSet ? `creator_${item.id}` : String(item.productId),
    item_name: item.name,
    item_category: item.isCustomSet ? 'creator_set' : 'menu',
    price: Number(item.price) || 0,
    quantity: Math.max(1, Math.floor(item.quantity)),
  }))
}

export function gaCartValue(items: CartItem[]): number {
  const v = items.reduce((s, i) => s + (Number(i.price) || 0) * (i.quantity || 1), 0)
  return Math.round(v * 100) / 100
}

export function trackViewCart(items: CartItem[], totalValue?: number): void {
  if (!window.gtag || !hasAnalyticsConsent()) return
  const value = typeof totalValue === 'number' ? totalValue : gaCartValue(items)
  window.gtag('event', 'view_cart', {
    currency: 'PLN',
    value,
    items: normalizeCartItemsForGa(items),
  })
}

export function trackViewItemList(
  products: { id: number; name: string; price: number; category?: string }[],
  listId: string,
  listName: string
): void {
  if (!window.gtag || !hasAnalyticsConsent()) return
  window.gtag('event', 'view_item_list', {
    item_list_id: listId,
    item_list_name: listName,
    items: products.map((p) => ({
      item_id: String(p.id),
      item_name: p.name,
      price: Number(p.price) || 0,
      item_category: p.category || 'menu',
    })),
  })
}

export function trackAddToCart(item: CartItem): void {
  if (!window.gtag || !hasAnalyticsConsent()) return
  const line = normalizeCartItemsForGa([item])[0]
  const value = (line.price || 0) * (line.quantity || 1)
  window.gtag('event', 'add_to_cart', {
    currency: 'PLN',
    value,
    items: [line],
  })
}

export function trackRemoveFromCart(item: CartItem | CartItem[]): void {
  if (!window.gtag || !hasAnalyticsConsent()) return
  const itemsArray = Array.isArray(item) ? item : [item]
  if (itemsArray.length === 0) return
  const lines = normalizeCartItemsForGa(itemsArray)
  const value = itemsArray.reduce((acc, curr) => acc + (Number(curr.price) || 0) * (curr.quantity || 1), 0)
  window.gtag('event', 'remove_from_cart', {
    currency: 'PLN',
    value,
    items: lines,
  })
}

export function trackBeginCheckout(items: CartItem[], value: number, deliveryMethod?: string): void {
  if (!window.gtag || !hasAnalyticsConsent()) return
  window.gtag('event', 'begin_checkout', {
    currency: 'PLN',
    value,
    items: normalizeCartItemsForGa(items),
    shipping_tier: deliveryMethod,
  })
}

export function trackAddPaymentInfo(
  paymentType: string,
  items: CartItem[],
  value: number
): void {
  if (!window.gtag || !hasAnalyticsConsent()) return
  window.gtag('event', 'add_payment_info', {
    currency: 'PLN',
    value,
    payment_type: paymentType,
    items: normalizeCartItemsForGa(items),
  })
}

/**
 * GA4 client id (for Measurement Protocol linkage). Best-effort; never throws.
 */
export function getGaClientId(): Promise<string | null> {
  return new Promise((resolve) => {
    if (!window.gtag || !hasAnalyticsConsent()) {
      resolve(null)
      return
    }
    try {
      let settled = false
      const finish = (v: string | null) => {
        if (settled) return
        settled = true
        resolve(v)
      }
      const g = window.gtag as ((
        a: string,
        b: string,
        c: string,
        d: (cid: string) => void
      ) => void)
      g('get', GA_MEASUREMENT_ID, 'client_id', (cid: string) => {
        finish(cid || null)
      })
      setTimeout(() => finish(null), 400)
    } catch {
      resolve(null)
    }
  })
}

export function refreshConsentFromStorage(): void {
  applyConsentFromCookie()
  if (hasAnalyticsConsent() || hasAdsConsent()) {
    injectGaScript()
    if (window.gtag) {
      window.gtag('js', new Date())
      window.gtag('config', GA_MEASUREMENT_ID, {
        send_page_view: false,
        anonymize_ip: true,
      })
    }
  }
}

let consentListenerAttached = false

/** Listen for post-consent updates without full reload (banner uses reload today — this is forward-compatible). */
export function subscribeConsentUpdates(): void {
  if (typeof window === 'undefined' || consentListenerAttached) return
  consentListenerAttached = true
  window.addEventListener('cookieConsentUpdated', () => {
    refreshConsentFromStorage()
  })
}
